import { getPlatformPage, PLATFORM_FEATURE_KEYS, PLATFORM_ROLE_KEYS } from "@/data/platformPages";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, forwardRef, lazy, Suspense } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { languages } from "@/i18n";
import { LocalizedLink } from "@/components/LocalizedLink";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useLocalePath } from "@/hooks/useLocalePath";
import { ROUTES, featurePath, solutionPath, whyUsPath } from "@/lib/routes";
const FooterNewsWidget = lazy(() => import("@/components/FooterNewsWidget").then(m => ({ default: m.FooterNewsWidget })));
import logo from "@/assets/logo.png?format=webp&quality=75&w=320";

const NewsletterSignup = forwardRef<HTMLFormElement>(function NewsletterSignup(_, ref) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consent) {
      toast({ title: t("footer.consentRequired"), description: t("footer.consentRequiredDesc"), variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const form = e.target as HTMLFormElement;
      const honeypot = new FormData(form).get("website") as string;
      const { data, error } = await supabase.functions.invoke("newsletter-signup", {
        body: { email, website: honeypot || "" },
      });
      if (error) throw error;
      if (data && !data.success) throw new Error(data.error);
      setSubmitted(true);
      toast({ title: t("footer.thankYou"), description: t("footer.subscribedSuccess") });
    } catch (err) {
      console.error("Newsletter signup error:", err);
      toast({ title: t("footer.errorTitle"), description: t("footer.errorDesc"), variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return <p className="text-sm text-teal-glow">{t("footer.subscribed")}</p>;
  }

  return (
    <form ref={ref} onSubmit={handleSubmit} className="space-y-3">
      <div className="flex gap-2">
        <Input
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          aria-label="Email address for newsletter"
          className="bg-white/5 border-white/10 text-white placeholder:text-white/50 h-9 text-sm"
        />
        <Button type="submit" size="sm" disabled={isSubmitting} className="shrink-0 h-9 px-3" aria-label="Subscribe to newsletter">
          {isSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
        </Button>
      </div>
      {/* Honeypot — hidden from humans */}
      <div className="absolute opacity-0 pointer-events-none" style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true" tabIndex={-1}>
        <Input name="website" tabIndex={-1} autoComplete="off" />
      </div>
      <div className="flex items-start gap-2">
        <Checkbox
          id="gdpr-consent"
          checked={consent}
          onCheckedChange={(v) => setConsent(v === true)}
          className="mt-0.5 border-white/20 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
        />
        <label htmlFor="gdpr-consent" className="text-xs text-white/60 leading-tight cursor-pointer">
          {t("footer.consentText")}{" "}
          <LocalizedLink to={ROUTES.privacy} className="text-white/60 hover:text-teal-glow underline underline-offset-2">{t("footer.privacyPolicy")}</LocalizedLink>.
        </label>
      </div>
    </form>
  );
});

// LanguageSwitcher is now imported from @/components/LanguageSwitcher

export function Footer() {
  const { t, i18n } = useTranslation();
  const pp = (k: string) => { const p = getPlatformPage(i18n.language, k); return { label: p.badge, href: p.path }; };

  const footerSections = [
    {
      title: t("footer.modules"),
      links: [
        { label: t("footer.allModules"), href: ROUTES.modules },
        { label: t("features.fleet"), href: featurePath("fleet") },
        { label: t("features.charter"), href: featurePath("charter") },
        { label: t("features.crm"), href: featurePath("crm") },
        { label: t("features.communications"), href: featurePath("communications") },
        { label: t("features.finance"), href: featurePath("finance") },
        { label: t("features.compliance"), href: featurePath("compliance") },
        { label: t("features.ais"), href: featurePath("ais") },
        { label: t("features.captainsBridge"), href: featurePath("captains-bridge") },
        { label: t("features.crewClients"), href: featurePath("crew-clients") },
        { label: t("features.ownerPortal"), href: featurePath("owner-portal") },
        ...PLATFORM_FEATURE_KEYS.map(pp),
        { label: t("features.documents"), href: featurePath("documents") },
      ],
    },
    {
      title: t("nav.management"),
      links: [
        { label: t("footer.managementOverview", "Management Overview"), href: ROUTES.management },
        { label: t("mgmtFeatures.crew"), href: featurePath("mgmt-crew") },
        { label: t("mgmtFeatures.fleet"), href: featurePath("mgmt-fleet") },
        { label: t("mgmtFeatures.owners"), href: featurePath("mgmt-owners") },
        { label: t("mgmtFeatures.pms"), href: featurePath("mgmt-pms") },
        { label: t("mgmtFeatures.compliance"), href: featurePath("mgmt-compliance") },
        { label: t("mgmtFeatures.claims"), href: featurePath("mgmt-claims") },
        { label: t("mgmtFeatures.finance"), href: featurePath("mgmt-finance") },
        { label: t("mgmtFeatures.provisioning"), href: featurePath("mgmt-provisioning") },
        { label: t("mgmtFeatures.signatures"), href: featurePath("mgmt-signatures") },
        { label: t("mgmtFeatures.comms"), href: featurePath("mgmt-comms") },
        { label: t("mgmtFeatures.docs"), href: featurePath("mgmt-docs") },
        { label: t("mgmtFeatures.tasks"), href: featurePath("mgmt-tasks") },
        { label: t("mgmtFeatures.reporting"), href: featurePath("mgmt-reporting") },
      ],
    },
    {
      title: t("nav.sales"),
      links: [
        { label: t("footer.salesOverview", "Sales Overview"), href: ROUTES.sales },
        { label: t("salesFeatures.listings"), href: featurePath("sales-listings") },
        { label: t("salesFeatures.sellerCrm"), href: featurePath("sales-seller-crm") },
        { label: t("salesFeatures.buyerCrm"), href: featurePath("sales-buyer-crm") },
        { label: t("salesFeatures.deals"), href: featurePath("sales-deals") },
        { label: t("salesFeatures.showings"), href: featurePath("sales-showings") },
        { label: t("salesFeatures.survey"), href: featurePath("sales-survey") },
        { label: t("salesFeatures.compliance"), href: featurePath("sales-compliance") },
        { label: t("salesFeatures.finance"), href: featurePath("sales-finance") },
        { label: t("salesFeatures.marketing"), href: featurePath("sales-marketing") },
        { label: t("salesFeatures.marketIntel"), href: featurePath("sales-market-intel") },
        { label: t("salesFeatures.ownerPortal"), href: featurePath("sales-owner-portal") },
        { label: t("salesFeatures.comms"), href: featurePath("sales-comms") },
        { label: t("salesFeatures.reporting"), href: featurePath("sales-reporting") },
        { label: t("salesFeatures.security"), href: featurePath("sales-security") },
      ],
    },
    {
      title: t("footer.services"),
      links: [
        { label: t("footer.servicesOverview", "Services Overview"), href: ROUTES.services },
        { label: t("footer.platformSetup", "Platform Setup"), href: `${ROUTES.services}#setup` },
        { label: t("footer.dataMigration", "Data Migration"), href: `${ROUTES.services}#import` },
        { label: t("footer.dataInput", "Data Input"), href: `${ROUTES.services}#input` },
        { label: t("footer.training", "Training"), href: `${ROUTES.services}#training` },
      ],
    },
    {
      title: t("nav.solutions"),
      links: [
        { label: t("nav.solutionsOverview"), href: ROUTES.solutions },
        { label: t("nav.solutionCharterBrokers"), href: solutionPath("charter-brokers") },
        { label: t("nav.solutionYachtBrokers"), href: solutionPath("yacht-brokers") },
        { label: t("nav.solutionYachtManagers"), href: solutionPath("yacht-managers") },
      ],
    },
    {
      title: t("nav.whyUs"),
      links: [
        { label: t("nav.whyUsAllInOne"), href: whyUsPath("all-in-one") },
        { label: t("nav.whyUsProfessionals"), href: whyUsPath("built-for-professionals") },
        { label: t("nav.whyUsSecurity"), href: whyUsPath("security") },
        { label: t("nav.whyUsMobile"), href: whyUsPath("mobile") },
        { label: t("seo.bestCharter.title"), href: "/best-yacht-charter-software" },
        { label: t("seo.bestManagement.title"), href: "/best-yacht-management-software" },
        { label: t("seo.bestSales.title"), href: "/best-yacht-sales-software" },
        { label: t("seo.captains.title"), href: "/yacht-software-for-captains" },
        ...PLATFORM_ROLE_KEYS.map(pp),
      ],
    },
    {
      title: t("footer.company"),
      links: [
        { label: t("nav.about"), href: ROUTES.about },
        { label: t("nav.careers"), href: ROUTES.careers },
        { label: t("nav.news"), href: ROUTES.news },
        { label: t("cta.bookDemo"), href: ROUTES.demo },
        { label: t("nav.contact"), href: ROUTES.contact },
        { label: t("footer.pressKit"), href: ROUTES.press },
      ],
    },
  ];

  return (
    <footer className="section-dark border-t border-white/10">
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 [&>*]:min-w-0 mb-10">
          <div className="col-span-2">
            <LocalizedLink to="/" className="flex items-center gap-2 mb-4">
              <img src={logo} alt="Seaventis" className="h-6 w-auto" width={137} height={15} />
            </LocalizedLink>
            <p className="text-sm text-white/70 leading-relaxed mb-6">
              {t("footer.tagline")}
            </p>
            <p className="font-display font-semibold text-sm text-white/90 mb-3" style={{ wordSpacing: '0.1em' }}>{t("footer.stayInLoop")}</p>
            <NewsletterSignup />
          </div>

          {footerSections.slice(0, 4).map((section) => (
            <div key={section.title}>
              <p className="font-display font-semibold text-sm text-white/90 mb-4">{section.title}</p>
              <ul className="space-y-2.5">
                {section.links.map((link) => (
                  <li key={link.href}>
                    <LocalizedLink to={link.href} className="text-sm text-white/60 hover:text-teal-glow transition-colors break-words hyphens-auto">{link.label}</LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 [&>*]:min-w-0 mb-12">
          <div className="hidden lg:block lg:col-span-2" />
          {footerSections.slice(4).map((section) => (
            <div key={section.title}>
              <p className="font-display font-semibold text-sm text-white/90 mb-4">{section.title}</p>
              <ul className="space-y-2.5">
                {section.links.map((link) => (
                  <li key={link.href}>
                    <LocalizedLink to={link.href} className="text-sm text-white/60 hover:text-teal-glow transition-colors break-words hyphens-auto">{link.label}</LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <Suspense fallback={null}><FooterNewsWidget /></Suspense>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <p className="text-xs text-white/60">© {new Date().getFullYear()} Seaventis. {t("footer.allRightsReserved")}</p>
            <LanguageSwitcher variant="full" />
          </div>
          <div className="flex items-center gap-5">
            <div className="flex items-center gap-3">
              <a href="https://facebook.com/seaventis" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-white/60 hover:text-white transition-colors">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.instagram.com/seaventis" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-white/60 hover:text-white transition-colors">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href="https://x.com/seaventis" target="_blank" rel="noopener noreferrer" aria-label="X" className="text-white/60 hover:text-white transition-colors">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
            </div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
              <LocalizedLink to={ROUTES.privacy} className="text-xs text-white/60 hover:text-white/80 transition-colors py-1">{t("footer.privacyPolicy")}</LocalizedLink>
              <LocalizedLink to={ROUTES.terms} className="text-xs text-white/60 hover:text-white/80 transition-colors py-1">{t("footer.termsOfService")}</LocalizedLink>
              <LocalizedLink to={ROUTES.sitemap} className="text-xs text-white/60 hover:text-white/80 transition-colors py-1">{t("sitemap.title")}</LocalizedLink>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
